import React, { useState, useEffect } from "react";
import clsx from "clsx";
import { useFormChangeHandler } from "hooks/useFormChangeHandler";
import { useSubmitHandler } from "hooks/useSubmitHandler";
import { useCreateData, useUpdateData } from "hooks/useApiHooks";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";

const CreatePage = ({ initialData, onSuccess, onError }) => {
  const API_URL = "/trip";

  // State untuk menyimpan form data. Jika initialData tersedia, isi form untuk update
  const [formData, setFormData] = useState({
    name: "",
    default_availability: 0,
    start_time: "",
    end_time: "",
  });

  const isUpdateMode = !!initialData; // Cek apakah ini update mode atau create mode

  // API hook untuk create dan update
  const { createData: createProduct, loading: creatingProduct } = useCreateData(
    API_URL,
    "trips"
  );
  const { updateData: updateProduct, loading: updatingProduct } = useUpdateData(
    `${API_URL}/${initialData?.id}`,
    "trips"
  );

  // Gunakan hook untuk menangani perubahan form
  // const handleChange = useFormChangeHandler(setFormData);
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: name === "default_availability" ? parseInt(value, 10) : value, // Konversi ke integer
    }));
};


  // Gunakan hook untuk menangani pengiriman form
  const { handleSubmit, isSubmitting, formError } = useSubmitHandler(
    isUpdateMode ? updateProduct : createProduct,
    onSuccess,
    onError
  );

  // Fungsi untuk konversi waktu dari "HH:mm:ss" ke "HH:mm"
  const formatTimeFromApi = (time) => {
    if (!time) return ""; // Jika waktu kosong, kembalikan string kosong
    return time.slice(0, 5); // Ambil bagian "HH:mm"
  };

  // Fungsi untuk konversi waktu dari "HH:mm" ke "HH:mm:ss"
  const formatTimeForApi = (time) => {
    if (!time) return ""; // Jika waktu kosong, kembalikan string kosong
    return `${time}:00`; // Tambahkan ":00" di akhir
  };

  const handleBeforeSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
    };

    handleSubmit(payload, e);
  };

  useEffect(() => {
    // Jika data produk berubah (untuk update), isi ulang form data
    // if (initialData) {
    //   setFormData({
    //     ...initialData,
    //   });
    //   console.log(initialData);
    // }
    if (initialData) {
      setFormData({
        ...initialData,
        start_time: formatTimeFromApi(initialData.start_time), // Konversi waktu ke format form
        end_time: formatTimeFromApi(initialData.end_time),     // Konversi waktu ke format form
      });
      console.log(initialData);
    }
  }, [initialData]);

  return (
    <form
      onSubmit={(e) => handleBeforeSubmit(e)}
      className="d-flex flex-column"
      style={{ height: "100%" }}
    >
      <div className="action-sheet-content flex-grow-1">
        {/* Label Name */}
        <div className="row mb-3 align-items-center">
          <label
            htmlFor="name"
            className="col-sm-4 col-form-label d-flex justify-content-end"
            style={{ fontSize: "14px" }}
          >
            Trip Name:
          </label>
          <div className="col-sm-8">
            <input
              type="text"
              className={clsx(
                "form-control gotra-form-control rounded-1",
                formError?.name && "is-invalid"
              )}
              id="name"
              placeholder="Trip Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={isSubmitting}
              style={{ fontSize: "14px", height: "35px" }}
              required
              minLength={3}
            />
            {formError?.name && (
              <div className="invalid-feedback">{formError.name}</div>
            )}
          </div>
        </div>

        {/* Default Availability */}
        <div className="row mb-3 align-items-center">
          <label
            htmlFor="default_availability"
            className="col-sm-4 col-form-label d-flex justify-content-end"
            style={{ fontSize: "14px" }}
          >
            Default Availability:
          </label>
          <div className="col-sm-8">
            <input
              type="number"
              className={clsx(
                "form-control gotra-form-control rounded-1",
                formError?.default_availability && "is-invalid"
              )}
              id="default_availability"
              placeholder="Default Availability"
              name="default_availability"
              value={formData.default_availability}
              onChange={handleChange}
              disabled={isSubmitting}
              style={{ fontSize: "14px", height: "35px" }}
              required
              min={1}
              max={10000}
            />
            {formError?.default_availability && (
              <div className="invalid-feedback">
                {formError.default_availability}
              </div>
            )}
          </div>
        </div>

        {/* Start Time */}
        <div className="row mb-3 align-items-center">
          <label
            htmlFor="start_time"
            className="col-sm-4 col-form-label d-flex justify-content-end"
            style={{ fontSize: "14px" }}
          >
            Start Time:
          </label>
          <div className="col-sm-8">
            <input
              type="time"
              className={clsx(
                "form-control gotra-form-control rounded-1",
                formError?.start_time && "is-invalid"
              )}
              id="start_time"
              placeholder="Start Time"
              name="start_time"
              value={formData.start_time}
              onChange={handleChange}
              disabled={isSubmitting}
              style={{ fontSize: "14px", height: "35px" }}
              required
            />
            {formError?.start_time && (
              <div className="invalid-feedback">{formError.start_time}</div>
            )}
          </div>
        </div>

        {/* End Time */}
        <div className="row mb-3 align-items-center">
          <label
            htmlFor="end_time"
            className="col-sm-4 col-form-label d-flex justify-content-end"
            style={{ fontSize: "14px" }}
          >
            End Time:
          </label>
          <div className="col-sm-8">
            <input
              type="time"
              className={clsx(
                "form-control gotra-form-control rounded-1",
                formError?.end_time && "is-invalid"
              )}
              id="end_time"
              placeholder="End Time"
              name="end_time"
              value={formData.end_time}
              onChange={handleChange}
              disabled={isSubmitting}
              style={{ fontSize: "14px", height: "35px" }}
              required
            />
            {formError?.end_time && (
              <div className="invalid-feedback">{formError.end_time}</div>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="form-group mt-auto d-grid">
          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={isSubmitting}
          >
            {isUpdateMode ? "Update Data" : "Create Data"}
          </button>
        </div>
      </div>
    </form>
  );
};

export default CreatePage;
